/**
 * Big: a machine for making a number enormous.
 *
 * Double it, and double it again. The arithmetic is done in BigInt, so the
 * four-hundredth press is still exactly right rather than rounded off — which
 * matters, because the entire pleasure of this toy is that it never gives up
 * before the child does.
 */

import { useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX } from 'preact';
import { Home, RotateCcw } from 'lucide-preact';
import type { Spark } from '../../engine/render.ts';
import { drawSparks, spawnSparks, updateSparks } from '../../engine/render.ts';
import { partyColors, token } from '../../engine/palette.ts';
import { play } from '../../engine/audio.ts';
import { useSurface } from '../../hooks/useSurface.ts';
import type { Operation } from '../../lib/bignum.ts';
import { apply, canApply, digitCount, groupDigits, landmarkFor, roughName, scaleName } from '../../lib/bignum.ts';
import { Coach, ToyButton } from '../../ui/controls.tsx';

interface Props {
  onExit: () => void;
  tabs: ComponentChildren;
}

const BUTTONS: Array<{ op: Operation; label: string; accent: string }> = [
  { op: 'plus1', label: '+ 1', accent: '--crayon-sky' },
  { op: 'double', label: 'double it', accent: '--crayon-teal' },
  { op: 'times10', label: 'x 10', accent: '--crayon-purple' },
  { op: 'square', label: 'times itself', accent: '--crayon-orange' },
  { op: 'half', label: 'half it', accent: '--crayon-yellow' },
];

/** Past this many digits, printing every one stops helping and starts hurting. */
const SHOW_ALL_BELOW = 200;

export function BigNumberToy({ onExit, tabs }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sparksRef = useRef<Spark[]>([]);
  const popRef = useRef(0);

  const [value, setValue] = useState(1n);
  const live = useRef(value);
  live.current = value;

  const digits = digitCount(value);
  const scale = scaleName(value);
  const landmark = landmarkFor(value);

  const run = (op: Operation) => {
    if (!canApply(live.current, op)) return;
    const next = apply(live.current, op);
    setValue(next);
    popRef.current = 1;
    play(op === 'half' ? 'pop' : 'count', 0.6);
    if (digitCount(next) > digitCount(live.current)) {
      const canvas = canvasRef.current;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        spawnSparks(sparksRef.current, { x: rect.width / 2, y: rect.height * 0.35 }, partyColors(), 18, 340);
      }
      play('sparkle', 0.5);
    }
  };

  useSurface(canvasRef, {
    onFrame: (ctx, dt, { width, height }) => {
      updateSparks(sparksRef.current, dt);
      popRef.current = Math.max(0, popRef.current - dt * 3);

      ctx.fillStyle = token('--paper');
      ctx.fillRect(0, 0, width, height);

      const text = digits <= SHOW_ALL_BELOW ? groupDigits(live.current) : `${live.current.toString().slice(0, 60)}…`;
      const top = 150;
      const bottom = height - 90;
      const boxW = width - 44;

      // Shrink to fit by measuring, not by guessing from the character count —
      // a guess that is slightly wrong wraps "4,194,304" into "4,194,30" and a
      // lonely "4", which is a worse way to show a number than a smaller one.
      let size = 72;
      const setSize = (s: number) => {
        size = s;
        ctx.font = `800 ${s}px ui-rounded, system-ui, sans-serif`;
      };
      setSize(size);
      while (size > 13 && ctx.measureText(text).width > boxW) setSize(size - 2);
      const lines = wrap(ctx, text, boxW);
      const lineHeight = size * 1.18;
      const blockH = lines.length * lineHeight;
      const startY = Math.max(top, (top + bottom) / 2 - blockH / 2) + size;

      ctx.save();
      const pop = 1 + popRef.current * 0.06;
      ctx.translate(width / 2, startY - size * 0.35);
      ctx.scale(pop, pop);
      ctx.translate(-width / 2, -(startY - size * 0.35));
      ctx.fillStyle = token('--ink');
      ctx.textAlign = 'center';
      lines.forEach((line, i) => ctx.fillText(line, width / 2, startY + i * lineHeight));
      ctx.restore();

      // How long it has become, once it is too long to take in at a glance.
      // Sits right under the number, where the coach line cannot land on it.
      if (digits > 6) {
        ctx.save();
        ctx.fillStyle = token('--ink-soft');
        ctx.textAlign = 'center';
        ctx.font = `700 ${Math.min(18, width * 0.045)}px ui-rounded, system-ui, sans-serif`;
        ctx.fillText(`${digits} digits long`, width / 2, Math.min(startY + blockH + 6, bottom));
        ctx.restore();
      }

      drawSparks(ctx, sparksRef.current);
    },
  });

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Big number machine" />
        <div class="topbar">
          <ToyButton round label="Home" hideLabel onClick={onExit}>
            <Home size={24} strokeWidth={2.5} />
          </ToyButton>
          <ToyButton
            round
            label="Back to one"
            hideLabel
            onClick={() => {
              setValue(1n);
              play('whoosh', 0.6);
            }}
          >
            <RotateCcw size={24} strokeWidth={2.5} />
          </ToyButton>
        </div>

        <p class="readout">
          <strong>{scale ? roughName(value) : 'keep going'}</strong>
          <span>{scale ? `that is a ${scale}` : 'press a button to make it bigger'}</span>
        </p>

        {landmark && <Coach hideAfter={9000}>{landmark}</Coach>}
      </div>

      <div class="dock">
        {tabs}
        <div class="dock__row dock__row--scroll" role="group" aria-label="Make it bigger">
          {BUTTONS.map((b) => (
            <ToyButton
              key={b.op}
              small
              label={b.label}
              accent={token(b.accent)}
              disabled={!canApply(value, b.op)}
              onClick={() => run(b.op)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Break a number that is too long for one line — at the thousands commas
 * wherever possible, so each line is still a readable group of digits rather
 * than an arbitrary slice through one.
 */
function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  if (ctx.measureText(text).width <= maxWidth) return [text];

  const groups = text.includes(',') ? text.split(',').map((g, i) => (i === 0 ? g : `,${g}`)) : [...text];
  const lines: string[] = [];
  let line = '';
  for (const group of groups) {
    if (line && ctx.measureText(line + group).width > maxWidth) {
      lines.push(line);
      line = '';
    }
    line += group;
  }
  if (line) lines.push(line);
  return lines.slice(0, 12);
}
